import { inject, Injectable, signal } from '@angular/core'
import { Kpi } from '../interfaces/kpis.interface'
import { firstValueFrom, forkJoin } from 'rxjs'
import { KpisService } from '../services/kpis.service'
import { months } from '@core/utils/circuits.utils'

@Injectable({
  providedIn: 'root'
})
export class KpisStore {
  private kpisService = inject(KpisService)

  private kpis$ = signal<Kpi[]>([])
  kpis = this.kpis$.asReadonly()

  currentKpiCategory = signal<string>(this.getCurrentCategory())

  /**
   * Carga los KPIs del usuario actual
   *
   * @param {string} uid - Identificador único del usuario
   * @param {string} category - Categoría de KPIs a cargar
   * @returns {Promise<void>}
   */
  async loadCurrentUserKpis(uid: string, category: string) {
    const kpis$ = this.kpisService.fetchKpis(category, uid, true)
    const kpis = await firstValueFrom(kpis$)

    this.setKpis(kpis)
  }

  /**
   * Carga los KPIs para múltiples usuarios
   *
   * @param {string[]} uids - Array de identificadores de usuarios
   * @param {string} category - Categoría de KPIs a cargar
   * @returns {Promise<void>}
   */
  async loadUsersKpis(uids: string[], category: string) {
    const kpis$ = uids.map(uid => this.kpisService.fetchKpis(category, uid, false))
    const kpisPromises = forkJoin(kpis$)
    const kpis = (await firstValueFrom(kpisPromises)).flat()

    this.setKpis(kpis)
  }

  /**
   * Carga los KPIs para un circuito específico y el usuario actual (logeado)
   *
   * @param {string} uid - Identificador único del usuario
   * @param {string} category - Categoría de KPIs a cargar
   * @returns {Promise<void>}
   */
  async loadCurrentUserCircuitKpis(uid: string, category: string) {
    const kpis$ = this.kpisService.fetchCircuitKpis('', uid, category, true)
    const kpis = await firstValueFrom(kpis$)

    this.setKpis(kpis)
  }

  async loadUsersCircuitKpis(uids: string[], category: string, date?: string) {
    const completeCategory = `${category}_${date}`

    const kpis$ = uids.map(uid => this.kpisService.fetchCircuitKpis('', uid, completeCategory, false))
    const kpisPromises = forkJoin(kpis$)
    const kpis = (await firstValueFrom(kpisPromises)).flat()

    this.setKpis(kpis)
  }

  /**
   * Obtiene los KPIs para un usuario y categoría específicos
   *
   * @param {string} uid - Identificador único del usuario
   * @param {string} category - Categoría de KPIs a obtener
   * @returns {Kpi[]} Array de KPIs filtrados
   */
  getKpis(uid: string, category: string): Kpi[] {
    return this.kpis$().filter(kpi => {
      const baseFilter = kpi.userUid === uid && kpi.category === category
      return baseFilter
    })
  }

  /**
   * Calcula la puntuación promedio para un conjunto de KPIs
   *
   * @param {Kpi[]} kpis - Array de KPIs para calcular puntuación
   * @returns {number} Puntuación promedio (porcentaje sobre 100)
   */
  getKpisScore(kpis: Kpi[]) {
    const acc = kpis.reduce((acc, value) => acc + value.percentage, 0)
    const score = kpis.length > 0 ? acc / kpis.length : 0

    return Number((score * 100).toFixed(2))
  }

  /**
   * Establece los KPIs en el store, asegurando que no haya duplicados
   * utilizando un mapa con claves únicas por KPI
   *
   * @param {Kpi[]} kpis - Array de KPIs a almacenar
   * @private
   */
  private setKpis(kpis: Kpi[]) {
    const existing = this.kpis$()
    const kpiMap = new Map<string, Kpi>()

    existing.forEach(kpi => {
      const key = this.generateKey(kpi)
      kpiMap.set(key, kpi)
    })

    kpis.forEach(kpi => {
      const key = this.generateKey(kpi)
      kpiMap.set(key, kpi)
    })

    this.kpis$.set(Array.from(kpiMap.values()))
  }

  /**
   * Genera una clave única para cada KPI basada en sus propiedades
   *
   * @param {Kpi} kpi - Objeto KPI
   * @returns {string} Clave única
   * @private
   */
  private generateKey(kpi: Kpi): string {
    return `${kpi.userUid}-${kpi.category}-${kpi.slug}`
  }

  private getCurrentCategory(): string {
    const date = new Date()
    const month = months[date.getMonth()]
    const year = date.getFullYear()

    return `kpi_${month}_${year}`
  }
}
